import type { FloorId } from '@/data/floors';
import { DAG_SLUTT_MINUTT, DAG_START_MINUTT } from '@/data/timeCosts';

/**
 * Bakgrunnshendelser (fase 4, Mad TV-mekanikk): ting som skjer i
 * Forlagshuset MENS spilleren er et annet sted. All vekting og alle
 * betingelser bor her — motoren (systems/bakgrunnsMotor.ts) inneholder
 * kun mekanikk. Dette er IKKE de interaktive event-kortene; de bor i
 * data/eventCatalog.ts.
 */

export interface BakgrunnsKontekst {
  dag: number;
  /** Minutter siden midnatt når hendelsen vurderes. */
  minutt: number;
  /** Rommet spilleren faktisk står i. */
  rom: FloorId;
  /** Antall manus spilleren har sendt tilbake (nølt med). */
  sendtTilbakeAntall: number;
  /** Har spilleren møtt agenten (fase 3-flagget)? */
  harMottAgent: boolean;
  /** Vekt-multiplikator for rival-hendelser (1 = normal, opp ved hevn/avsløring). */
  rivalVektFaktor: number;
  /** Har rivalen allerede et aktivt bud ute? */
  harAktivtRivalBud: boolean;
  /** Har spilleren bøker i produksjon (mål for kolliderende lansering)? */
  harBokIProduksjon: boolean;
}

export type BakgrunnsEffekt =
  | 'bonus-manus'
  | 'rival-bud'
  | 'rival-lansering'
  | 'kd-kontakt';

// ---- Rival-balansering (5C) — juster her, ikke i scener ----
/** Spilldager spilleren har på å matche rivalens bud. */
export const RIVAL_BUD_FRIST_DAGER = 3;
/** Hvor lenge en kolliderende KD-lansering demper salget. */
export const RIVAL_LANSERING_DAGER = 14;
/** Salgsmultiplikator i kollisjonsvinduet (0.85 = -15 %). */
export const RIVAL_LANSERING_FAKTOR = 0.85;
/** Samtaler i kantina før KD-forfatteren kan tilbys kontrakt (5D). */
export const KANTINE_SAMTALER_KRAV = 3;
/** Krav redusert hvis den misfornøyde tok kontakt først ('kd_kontakt'-flagget). */
export const KANTINE_SAMTALER_KRAV_ETTER_KONTAKT = 2;
/** Forskudd for å signere en forfatter fra KD-stallen (5D). */
export const STALL_KONTRAKT_PRIS = 40_000;

export interface BakgrunnsHendelseDef {
  id: string;
  tittel: string;
  /** Linjen i dagsoppsummeringen. */
  oppsummering: string;
  /** Tekst på beskjedlapp i resepsjonen dagen etter (valgfri). */
  beskjedlapp?: string;
  /** Relativ sannsynlighet i det vektede valget. */
  vekt: number;
  /** Antall dager før samme hendelse kan trigges igjen. */
  cooldownDager: number;
  /** Mekanisk effekt (utover flavor). Påføres av scenen, definert her. */
  effekt?: BakgrunnsEffekt;
  /** 'rival' vektes med ctx.rivalVektFaktor (hevn-/spionasje-modus). */
  kategori?: 'rival';
  betingelse?: (ctx: BakgrunnsKontekst) => boolean;
}

/** Lengden på spilldagen i minutter — basis for trigger-sannsynlighet. */
export const SPILLDAG_MINUTTER = DAG_SLUTT_MINUTT - DAG_START_MINUTT;

/** Forventet antall bakgrunnshendelser per spilldag. */
export const HENDELSER_PER_DAG = 2;

export const BAKGRUNNS_HENDELSER: BakgrunnsHendelseDef[] = [
  {
    id: 'telefon-ubesvart',
    tittel: 'Telefonen ringte',
    oppsummering:
      'Telefonen på kontoret ditt ringte og ringte. Ingen svarte. Det føltes viktig.',
    beskjedlapp:
      'Ubesvart anrop til kontoret ditt: «Ring tilbake. Det haster.» Ikke noe nummer.',
    vekt: 3,
    cooldownDager: 1,
    betingelse: (ctx) => ctx.rom !== 'mittKontor',
  },
  {
    id: 'konkurrent-signerte',
    tittel: 'Kapelle Damm slo til',
    oppsummering:
      'Kapelle Damm signerte en forfatter du nølte med. Agenten lot deg ikke engang få vite det først.',
    vekt: 2,
    cooldownDager: 3,
    betingelse: (ctx) => ctx.sendtTilbakeAntall > 0,
  },
  {
    id: 'manusbunke-vokser',
    tittel: 'Manusbunken vokste',
    oppsummering:
      'Manusbunken hos redaktøren vokste mens du var ute. Én av dem ser faktisk lovende ut.',
    vekt: 3,
    cooldownDager: 1,
    effekt: 'bonus-manus',
    betingelse: (ctx) => ctx.rom !== 'redaksjon',
  },
  {
    id: 'journalist-beskjed',
    tittel: 'Journalist innom',
    oppsummering:
      'En journalist stakk innom resepsjonen, ventet i ti minutter og la igjen en beskjed.',
    beskjedlapp:
      'Fra journalisten i Klassekampen: «Vil ha en kommentar om høstlistene. Ring meg i morgen tidlig.»',
    vekt: 2,
    cooldownDager: 2,
  },
  {
    id: 'agent-tips',
    tittel: 'Agenten prøvde å nå deg',
    oppsummering:
      'Agent Thomas prøvde å få tak i deg — han hadde «noe stort». Nå tar han ikke telefonen.',
    beskjedlapp:
      'Agent Thomas: «Hadde noe stort til deg. Førstemann til mølla. Du vet hvor jeg er 10–12.»',
    vekt: 2,
    cooldownDager: 2,
    betingelse: (ctx) => ctx.harMottAgent,
  },
  {
    id: 'kaffemaskin-rykte',
    tittel: 'Rykter ved kaffemaskinen',
    oppsummering:
      'Ved kaffemaskinen gikk ryktene om en debutant «alle» vil ha. Ingen husker navnet.',
    vekt: 1,
    cooldownDager: 2,
  },
  // ---- Rival-hendelser (5C) — Kapelle Damm gjør faktiske trekk ----
  {
    id: 'rival-byr',
    tittel: 'Kapelle Damm byr',
    oppsummering:
      'Kapelle Damm la inn bud på et manus du sendte tilbake. Agenten gir deg noen dager før de signerer.',
    beskjedlapp:
      'Agenten: «KD byr på manuset du nølte med. Du har noen dager på deg — så signerer de.»',
    vekt: 2,
    cooldownDager: 4,
    kategori: 'rival',
    effekt: 'rival-bud',
    betingelse: (ctx) => ctx.sendtTilbakeAntall > 0 && !ctx.harAktivtRivalBud,
  },
  {
    id: 'rival-lansering',
    tittel: 'Kolliderende lansering',
    oppsummering:
      'Kapelle Damm rykket frem en stor lansering i samme sjanger som din kommende utgivelse.',
    vekt: 2,
    cooldownDager: 5,
    kategori: 'rival',
    effekt: 'rival-lansering',
    betingelse: (ctx) => ctx.harBokIProduksjon,
  },
  {
    id: 'rival-misfornoyd',
    tittel: 'Anonym telefon',
    oppsummering:
      'Resepsjonen tok imot en telefon fra en forfatter som ikke ville si navnet sitt.',
    beskjedlapp:
      'Beskjed: «En forfatter ringte. Ville ikke si navnet sitt. Sa han ‘vurderer alternativer’ og at han spiser lunsj i kantina.»',
    vekt: 1,
    cooldownDager: 6,
    kategori: 'rival',
    effekt: 'kd-kontakt',
  },
];

export function finnBakgrunnsHendelse(id: string): BakgrunnsHendelseDef | undefined {
  return BAKGRUNNS_HENDELSER.find((h) => h.id === id);
}
